import { ArrowDownIcon, ArrowLeftIcon, ArrowUpIcon } from "lucide-react";
import { AmcLogo } from "web-core/components";
import { BENCHMARK_OPTIONS } from "@/modules/funds/components/baskets/fund-basket-backtest-chart";
import {
  getBacktestStepUpParams,
  useProposalBuilderContext,
  useProposalFormContext,
} from "@planning/providers/proposal-provider";
import { useProposalBacktest } from "creso-core/hooks";
import {
  convertToCurrency,
  formatAsCrore,
  formatAsPercentage,
} from "creso-core/utils";
import { type ComponentProps, useId, useMemo } from "react";
import { Area, ComposedChart, Line, XAxis, YAxis } from "recharts";
import type { TooltipProps } from "recharts";
import { format, formatDate } from "date-fns";
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  cn,
} from "web-design-system";
import { GoalCategory } from "creso-core/types";
import { GoalIcon } from "web-core";
import { StepUpLabel } from "@/modules/reports/components/proposal/step-up-label";

const BACKTEST_PERIOD_OPTIONS = [
  { label: "1 Year", value: "1Y" },
  { label: "3 Years", value: "3Y" },
  { label: "5 Years", value: "5Y" },
  { label: "10 Years", value: "10Y" },
];

const PORTFOLIO_STROKE = "var(--chart-3)";
const BENCHMARK_STROKE = "var(--muted-foreground)";

const chartConfig = {
  portfolio: { label: "Portfolio average", color: PORTFOLIO_STROKE },
  benchmark: { label: "Benchmark", color: BENCHMARK_STROKE },
} satisfies ChartConfig;

type SeriesPoint = {
  date: string;
  portfolio_value: number;
  total_invested: number;
};

type ChartPoint = {
  date: string;
  portfolio?: number;
  benchmark?: number;
  portfolio_invested?: number;
  benchmark_invested?: number;
};

function mergeChartSeries(
  portfolioSeries: SeriesPoint[],
  benchmarkSeries: SeriesPoint[]
): ChartPoint[] {
  const map = new Map<string, Omit<ChartPoint, "date">>();
  portfolioSeries.forEach((p) => {
    const cur = map.get(p.date) ?? {};
    cur.portfolio = p.portfolio_value;
    cur.portfolio_invested = p.total_invested;
    map.set(p.date, cur);
  });
  benchmarkSeries.forEach((p) => {
    const cur = map.get(p.date) ?? {};
    cur.benchmark = p.portfolio_value;
    cur.benchmark_invested = p.total_invested;
    map.set(p.date, cur);
  });
  return [...map.entries()]
    .sort((a, b) => new Date(a[0]).getTime() - new Date(b[0]).getTime())
    .map(([date, v]) => ({ date, ...v }));
}

export function ProposalBacktest() {
  const { prev } = useProposalBuilderContext();
  const { watch } = useProposalFormContext();

  const category = watch("goal.category");
  const proposalFunds = watch("proposal_funds");
  const totalLumpsum = watch("total_lumpsum_amount");
  const totalSip = watch("total_sip_amount");
  const proposalId = watch("id");
  const stepUpAmount = watch("step_up_amount");
  const stepUpPercentage = watch("step_up_percentage");
  const stepUpFrequency = watch("step_up_frequency");

  const hasLumpsum = Number(totalLumpsum) > 0;
  const hasSip = Number(totalSip) > 0;

  const lumpsumFunds = proposalFunds.filter((f) => f.type === "LUMPSUM");
  const sipFunds = proposalFunds.filter((f) => f.type === "SIP");

  const stepUpParams = getBacktestStepUpParams(
    {
      step_up_amount: stepUpAmount,
      step_up_percentage: stepUpPercentage,
      step_up_frequency: stepUpFrequency,
    },
    hasSip ? sipFunds.length : 0
  );

  return (
    <div className="flex size-full">
      <div className="w-75 p-4 xl:w-90 max-w-1/2 shrink-0 gap-4 bg-card border-r border-secondary flex flex-col">
        <button
          onClick={prev}
          className="flex items-center gap-2 p-1.5 text-sm font-medium hover:bg-secondary/50 transition-colors border border-border rounded-lg text-left bg-transparent"
        >
          <div className="size-9 flex items-center justify-center">
            <ArrowLeftIcon className="size-5 shrink-0" />
          </div>
          <span className="mr-auto">Continue Editing Proposal</span>
          <GoalIcon category={category as GoalCategory} />
        </button>

        <div className="flex-1 overflow-y-auto flex flex-col gap-4">
          {hasLumpsum && lumpsumFunds.length > 0 && (
            <div className="bg-background border border-secondary rounded-lg p-4 flex flex-col gap-3">
              <span className="font-semibold text-sm">One-time</span>
              {lumpsumFunds.map((fund) => (
                <div
                  key={`lumpsum-${fund.fund_scheme}`}
                  className="flex items-center gap-3"
                >
                  <AmcLogo
                    amc_code={fund.amc_code ?? ""}
                    size="sm"
                    className="shrink-0"
                  />
                  <span className="flex-1 text-sm truncate">
                    {fund.fund_scheme_name}
                  </span>
                  <span className="text-sm text-muted-foreground shrink-0">
                    {formatAsCrore(fund.lumpsum_amount as string)}
                  </span>
                </div>
              ))}
            </div>
          )}

          {hasSip && sipFunds.length > 0 && (
            <div className="bg-background border border-secondary rounded-lg p-4 flex flex-col gap-3">
              <span className="font-semibold text-sm">Monthly SIP</span>
              {sipFunds.map((fund) => (
                <div
                  key={`sip-${fund.fund_scheme}`}
                  className="flex items-center gap-3"
                >
                  <AmcLogo
                    amc_code={fund.amc_code ?? ""}
                    size="sm"
                    className="shrink-0"
                  />
                  <span className="flex-1 text-sm truncate">
                    {fund.fund_scheme_name}
                  </span>
                  <span className="text-sm text-muted-foreground shrink-0">
                    {formatAsCrore(fund.sip_amount as string)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {proposalId ? (
          <BacktestContent
            proposalId={proposalId}
            totalLumpsum={totalLumpsum}
            totalSip={totalSip}
            hasLumpsum={hasLumpsum}
            hasSip={hasSip}
            stepUpParams={stepUpParams}
            stepUp={{
              step_up_amount: stepUpAmount?.toString() ?? null,
              step_up_percentage: stepUpPercentage?.toString() ?? null,
              step_up_frequency: stepUpFrequency,
              total_sip_amount: totalSip,
            }}
          />
        ) : null}
      </div>
    </div>
  );
}

function BacktestContent({
  proposalId,
  totalLumpsum,
  totalSip,
  hasLumpsum,
  hasSip,
  stepUpParams,
  stepUp,
}: {
  proposalId: number;
  totalLumpsum: string;
  totalSip: string;
  hasLumpsum: boolean;
  hasSip: boolean;
  stepUpParams: ReturnType<typeof getBacktestStepUpParams>;
  stepUp: ComponentProps<typeof StepUpLabel>["proposal"];
}) {
  const uid = useId().replace(/:/g, "");
  const {
    backtestPeriod: period,
    backtestBenchmark: benchmark,
    setBacktestPeriod: setPeriod,
    setBacktestBenchmark: setBenchmark,
  } = useProposalBuilderContext();

  const { data, isLoading } = useProposalBacktest({
    id: proposalId,
    period,
    sipFrequency: "MONTHLY",
    benchmark,
    ...stepUpParams,
  });

  const chartPoints = useMemo(() => {
    if (!data) return [];
    return mergeChartSeries(data.proposal_series, data.benchmark_series);
  }, [data]);

  const benchmarkLabel =
    BENCHMARK_OPTIONS.find((opt) => opt.value === benchmark)?.label ??
    benchmark;

  const formatXAxisTick = (dateStr: string) => {
    try {
      return format(new Date(dateStr), "MMM yy");
    } catch {
      return "";
    }
  };

  return (
    <div className="flex flex-col p-6 gap-6">
      <span className="text-lg font-semibold">Test against Benchmark</span>

      <div className="rounded-lg flex flex-col p-5 pt-3 gap-1 bg-muted">
        <div className="grid grid-cols-2 divide-x divide-border gap-4 items-start">
          <div className="flex flex-row gap-4 items-start col-span-1">
            <div className="flex flex-col truncate flex-1 gap-1 justify-between">
              <span className="truncate text-xs text-muted-foreground">
                One time
              </span>
              <span className="font-semibold truncate flex items-center text-sm h-10">
                {hasLumpsum ? formatAsCrore(Number(totalLumpsum), 0) : "—"}
              </span>
            </div>
            <div className="flex flex-col flex-1 truncate gap-1 justify-between">
              <span className="text-xs truncate text-muted-foreground">
                Monthly SIP
              </span>
              <span className="font-semibold truncate flex items-center text-sm h-10">
                {hasSip ? formatAsCrore(Number(totalSip), 0) : "-"}
              </span>
              <StepUpLabel proposal={stepUp} />
            </div>
          </div>

          {/* <div className="w-px self-stretch bg-border mx-2" /> */}

          <div className="flex flex-col gap-2">
            <div className="col-span-1 flex flex-row gap-4 items-start">
              <div className="flex flex-col flex-1 shrink-0 gap-1 justify-between">
                <span className="text-xs text-muted-foreground truncate">
                  Time period
                </span>
                <Select value={period} onValueChange={setPeriod}>
                  <SelectTrigger className="h-9 w-full px-4">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {BACKTEST_PERIOD_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-col flex-1 gap-1 shrink-0">
                <span className="text-xs truncate text-muted-foreground">
                  Compare against
                </span>
                <Select value={benchmark} onValueChange={setBenchmark}>
                  <SelectTrigger className="h-9 px-4 w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {BENCHMARK_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {data?.actual_start_date && data?.actual_end_date ? (
              <span className="text-xs text-right text-muted-foreground">
                Date overlap &ndash;{" "}
                {formatDate(data.actual_start_date, "MMM dd, yyyy")} to{" "}
                {formatDate(data.actual_end_date, "MMM dd, yyyy")}
              </span>
            ) : null}
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="flex h-60 items-center justify-center text-sm text-muted-foreground">
          Loading backtest data...
        </div>
      ) : chartPoints.length > 0 ? (
        <ChartContainer
          config={chartConfig}
          className="aspect-auto h-80 w-full"
        >
          <ComposedChart
            data={chartPoints}
            margin={{ top: 8, right: 8, bottom: 28, left: 4 }}
          >
            <defs>
              <linearGradient
                id={`backtestPortfolioFill-${uid}`}
                x1="0"
                y1="0"
                x2="0"
                y2="1"
              >
                <stop
                  offset="5%"
                  stopColor={PORTFOLIO_STROKE}
                  stopOpacity={0.4}
                />
                <stop
                  offset="95%"
                  stopColor={PORTFOLIO_STROKE}
                  stopOpacity={0.05}
                />
              </linearGradient>
            </defs>
            <YAxis hide domain={["auto", "auto"]} />
            <XAxis
              dataKey="date"
              domain={["dataMin", "dataMax"]}
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              tickFormatter={formatXAxisTick}
              interval="preserveStartEnd"
              minTickGap={40}
            />
            <ChartTooltip
              cursor={false}
              content={<BacktestTooltip benchmarkLabel={benchmarkLabel} />}
            />
            <Line
              type="monotone"
              dataKey="benchmark"
              stroke={BENCHMARK_STROKE}
              strokeWidth={1.5}
              strokeDasharray="3 3"
              dot={false}
              connectNulls
              isAnimationActive
            />
            <Area
              type="monotone"
              dataKey="portfolio"
              stroke={PORTFOLIO_STROKE}
              strokeWidth={2}
              fill={`url(#backtestPortfolioFill-${uid})`}
              dot={false}
              connectNulls
              isAnimationActive
            />
          </ComposedChart>
        </ChartContainer>
      ) : (
        <div className="flex h-60 items-center justify-center rounded-lg border border-dashed text-sm text-muted-foreground">
          No backtest data available for the selected period.
        </div>
      )}

      {data && (
        <BacktestStatsTable
          proposalMetrics={data.proposal_metrics}
          benchmarkMetrics={data.benchmark_metrics}
          benchmarkLabel={benchmarkLabel}
        />
      )}
    </div>
  );
}

function BacktestTooltip({
  active,
  payload,
  benchmarkLabel,
}: TooltipProps<number, string> & { benchmarkLabel: string }) {
  if (!active || !payload?.length) return null;
  const point = payload[0]?.payload as ChartPoint | undefined;
  if (!point) return null;

  const parsed = Date.parse(point.date);
  const formattedDate = Number.isNaN(parsed)
    ? point.date
    : format(parsed, "MMM dd, yyyy");

  const invested = point.portfolio_invested ?? point.benchmark_invested;

  return (
    <div className="border-border/50 bg-background min-w-64 rounded-md border p-3 text-xs shadow">
      <div className="text-xs font-semibold text-foreground">
        {formattedDate}
      </div>
      {typeof invested === "number" && (
        <div className="mt-1.5 text-muted-foreground">
          Invested:{" "}
          <span className="text-foreground">
            {convertToCurrency(invested, 0)}
          </span>
        </div>
      )}
      <div className="my-3 h-px bg-border" />
      <div className="flex flex-col gap-2.5">
        <BacktestTooltipRow
          color={PORTFOLIO_STROKE}
          name="Portfolio average"
          portfolioValue={point.portfolio}
          totalInvested={point.portfolio_invested}
        />
        <BacktestTooltipRow
          color={BENCHMARK_STROKE}
          name={benchmarkLabel}
          portfolioValue={point.benchmark}
          totalInvested={point.benchmark_invested}
        />
      </div>
    </div>
  );
}

function BacktestTooltipRow({
  color,
  name,
  portfolioValue,
  totalInvested,
}: {
  color: string;
  name: string;
  portfolioValue: number | undefined;
  totalInvested: number | undefined;
}) {
  const hasValue = typeof portfolioValue === "number";
  const hasReturn =
    hasValue && typeof totalInvested === "number" && totalInvested > 0;
  const returnPct = hasReturn
    ? ((portfolioValue - totalInvested) / totalInvested) * 100
    : undefined;
  const sign = hasReturn
    ? returnPct! > 0
      ? "+"
      : returnPct! < 0
        ? "-"
        : ""
    : "";

  return (
    <div className="flex items-center gap-2">
      <span
        aria-hidden
        className="h-2.5 w-2.5 shrink-0 rounded-[3px]"
        style={{ backgroundColor: color }}
      />
      <span className="flex-1 text-foreground">{name}</span>
      {hasValue && (
        <span className="font-medium tabular-nums text-foreground">
          {convertToCurrency(portfolioValue, 0)}
        </span>
      )}
      {hasReturn && (
        <span
          data-sign={sign}
          className={cn(
            "flex items-center gap-0.5 font-medium tabular-nums",
            "data-[sign='+']:text-constructive data-[sign='-']:text-destructive",
            sign === "" && "text-muted-foreground"
          )}
        >
          {sign === "+" && <ArrowUpIcon className="size-3" />}
          {sign === "-" && <ArrowDownIcon className="size-3" />}
          {Math.abs(returnPct!).toFixed(2)}%
        </span>
      )}
    </div>
  );
}

type ProposalMetrics = {
  total_invested: number;
  final_value: number;
  xirr: number;
  standard_deviation: number;
  max_drawdown: number;
};

function BacktestStatsTable({
  proposalMetrics,
  benchmarkMetrics,
  benchmarkLabel,
}: {
  proposalMetrics: ProposalMetrics;
  benchmarkMetrics: ProposalMetrics;
  benchmarkLabel: string;
}) {
  return (
    <div className="rounded-xl border border-border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-1/3" />
            <TableHead className="text-left">
              <div className="flex items-center gap-2">
                <span className="inline-block size-3 rounded-sm bg-chart-3" />
                <span className="text-sm font-medium">Portfolio average</span>
              </div>
            </TableHead>
            <TableHead className="text-left">
              <div className="flex items-center gap-2">
                <span className="inline-block w-4 border-t-2 border-dashed border-muted-foreground" />
                <span className="text-sm font-medium">{benchmarkLabel}</span>
              </div>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          <TableRow>
            <TableCell className="text-muted-foreground">
              Total invested
            </TableCell>
            <TableCell className="text-left">
              {formatAsCrore(proposalMetrics.total_invested)}
            </TableCell>
            <TableCell className="text-left">
              {formatAsCrore(benchmarkMetrics.total_invested)}
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell className="text-muted-foreground">Final value</TableCell>
            <TableCell className="text-left">
              {formatAsCrore(proposalMetrics.final_value)}
            </TableCell>
            <TableCell className="text-left">
              {formatAsCrore(benchmarkMetrics.final_value)}
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell className="text-muted-foreground">XIRR</TableCell>
            <TableCell
              className={cn(
                "text-left",
                proposalMetrics.xirr > 0
                  ? "text-constructive"
                  : "text-destructive",
                proposalMetrics.xirr === 0 && "text-muted-foreground"
              )}
            >
              {proposalMetrics.xirr > 0 ? "+" : ""}
              {formatAsPercentage(proposalMetrics.xirr, "-", 1)}
            </TableCell>
            <TableCell
              className={cn(
                "text-left",
                benchmarkMetrics.xirr > 0
                  ? "text-constructive"
                  : "text-destructive",
                benchmarkMetrics.xirr === 0 && "text-muted-foreground"
              )}
            >
              {benchmarkMetrics.xirr > 0 ? "+" : ""}
              {formatAsPercentage(benchmarkMetrics.xirr, "-", 1)}
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell className="text-muted-foreground">
              Standard deviation
            </TableCell>
            <TableCell className="text-left">
              {proposalMetrics.standard_deviation.toFixed(2)}
            </TableCell>
            <TableCell className="text-left">
              {benchmarkMetrics.standard_deviation.toFixed(2)}
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell className="text-muted-foreground">
              Max drawdown
            </TableCell>
            <TableCell className="text-left">
              {formatAsPercentage(proposalMetrics.max_drawdown, "-", 2)}
            </TableCell>
            <TableCell className="text-left">
              {formatAsPercentage(benchmarkMetrics.max_drawdown, "-", 2)}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </div>
  );
}
